import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import { alpha } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import { RandomAvatar } from "react-random-avatars";
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { setAccounts, setAccount, setSites } from '../../../app/appSlice';
import { useTranslations } from '../../../hooks/use-translations';
import LanguageSelector from '../../../components/language-selector';

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const [open, setOpen] = useState(null);
  const auth = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accounts, account } = useSelector((state) => state.app);
  const { t } = useTranslations();

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleLogOut = () => {
    auth.signoutSilent();
    auth.removeUser();
  };

  const handleSwitchAccount = () => {
    handleClose();
    navigate('/switch-account');
  };

  const handleAccountSettings = () => {
    handleClose();
    navigate('/account-settings');
  };

  const roles = JSON.parse(atob(auth.user.access_token.split('.')[1])).realm_access.roles;
  const isAdmin = roles.includes('admin');

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          ...(open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          }),
        }}
      >
        <Avatar
          src="/assets/images/avatars/user.png"
          alt={auth.user?.profile.name}
          sx={{
            width: 36,
            height: 36,
            border: (theme) => `solid 2px ${theme.palette.background.default}`,
          }}
        >
          {auth.user?.profile.name.charAt(0).toUpperCase()}
        </Avatar>
      </IconButton>

      <Popover
        open={!!open}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 0,
            mt: 1,
            ml: 0.75,
            width: 300,
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2 }}>
          <Typography variant="subtitle2" noWrap>
            {auth.user?.profile.name}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {t('accountPopover.email')}: {auth.user?.profile.email}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {t('accountPopover.account')}: {account?.name}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed', m: 0 }} />

        {/* TODO ENABLE WHEN READY*/}
        {/* Language Selector */}
        {/*<LanguageSelector onClose={handleClose} />*/}
        {/*<Divider sx={{ borderStyle: 'dashed', m: 0 }} />*/}

        {isAdmin && (
          <MenuItem
            disableRipple
            disableTouchRipple
            onClick={handleAccountSettings}
            sx={{ typography: 'body2', color: 'text.secondary', py: 1.5 }}
          >
            <ListItemIcon>
              <Icon icon="material-symbols:settings" width="24" height="24" />
            </ListItemIcon>
            <ListItemText></ListItemText>
            <Typography variant="body2">
              {t('accountPopover.accountSettings')}
            </Typography>
          </MenuItem>
        )}

        {accounts.length > 1 && (
          <>
            <MenuItem
              disableRipple
              disableTouchRipple
              onClick={handleSwitchAccount}
              sx={{ typography: 'body2', color: 'text.secondary', py: 1.5 }}
            >
              <ListItemIcon>
                <Icon icon="mdi:account-switch-outline" width="24" height="24" />
              </ListItemIcon>
              <ListItemText></ListItemText>
              <Typography variant="body2">
                {t('accountPopover.switchAccount')}
              </Typography>
            </MenuItem>
            <Divider sx={{ borderStyle: 'dashed', mt: 0, mb: 0 }} style={{ marginTop: 0, marginBottom: 0 }} />
          </>
        )}

        <MenuItem
          disableRipple
          disableTouchRipple
          onClick={handleLogOut}
          sx={{ typography: 'body2', color: 'red', py: 1.5 }}
        >
          <ListItemIcon>
            <Icon icon="hugeicons:logout-01" style={{ color: 'red' }} width="24" height="24" />
          </ListItemIcon>
          <ListItemText></ListItemText>
          <Typography variant="body2">
            {t('accountPopover.logout')}
          </Typography>
        </MenuItem>
      </Popover>
    </>
  );
}
